import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoClose, IoTrashOutline, IoCreateOutline } from "react-icons/io5";
import { getWorksByWorkOrderId, updateWork, addWork, getWorkOrderById } from '../../services/AppService';
import closeButton from "../../assets/images/close.png";
import PageFade from '../../components/Login/PageFade';
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import { resolveMediaUrl } from '../../utility/media';
import ConfirmActionModal from '../../components/AdminComponents/Modals/ConfirmActionModal';

const AddWorkForm = () => {
    const { workOrderId } = useParams();
    const { state } = useLocation();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [subWorks, setSubWorks] = useState([]);
    const [workOrder, setWorkOrder] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [isHoveringDetails, setIsHoveringDetails] = useState(false);

    const [formData, setFormData] = useState({
        subWorkName: "",
        subWorkDetails: "",
        startDate: "",
        endDate: "",
        fileName: "", // Track existing file name
        fileUrl: ""
    });
    const [selectedFile, setSelectedFile] = useState(null);

    const [confirmModal, setConfirmModal] = useState({ open: false });
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    const loadData = useCallback(async () => {
        try {
            // Fetch both the parent work order details and the list of sub-works
            const [parentDetails, list] = await Promise.all([
                getWorkOrderById(workOrderId),
                getWorksByWorkOrderId(workOrderId)
            ]);
            setWorkOrder(parentDetails);
            setSubWorks(list || []);
        } catch (error) {
            console.error("Failed to load data", error);
        }
    }, [workOrderId]);

    useEffect(() => {
        if (workOrderId) loadData();
    }, [workOrderId, loadData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setConfirmModal({ open: true });
    };

    // 2. Triggered by "Confirm" button in Modal
    const handleConfirmSave = async () => {
        setConfirmModal({ open: false }); // Close confirmation first
        const data = new FormData();
        const payload = { ...formData, workOrderId: Number(workOrderId) };

        data.append("work", new Blob([JSON.stringify(payload)], { type: "application/json" }));
        if (selectedFile) data.append("file", selectedFile);

        try {
            if (editingId) {
                await updateWork(editingId, data);
                showMsg("success", "Success", "Sub Work Details Updated Successfully");
            } else {
                await addWork(data);
                showMsg("success", "Success", "Sub Work Details Added Successfully");
            }
            resetForm();
            loadData();
        } catch (error) {
            showMsg("error", "Error", error.response?.data?.message || "Action Failed");
        }
    };

    const showMsg = (type, title, message) => setMessageModal({ open: true, type, title, message });

    const handleEdit = (item) => {
        setEditingId(item.id);
        setFormData({
            subWorkName: item.subWorkName,
            subWorkDetails: item.subWorkDetails,
            startDate: item.startDate, // Fix date display in input
            endDate: item.endDate,    // Fix date display in input
            fileName: item.fileName, // Store existing filename
            fileUrl: item.fileUrl
        });
        setSelectedFile(null);
    };

    const resetForm = () => {
        setFormData({ subWorkName: "", subWorkDetails: "", startDate: "", endDate: "", fileName: "", fileUrl: "" });
        setSelectedFile(null);
        setEditingId(null);
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = "";
    };

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-4 md:p-6 relative">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h4 className="text-lg md:text-xl font-semibold text-blue">{t('pageTitles.workList')}</h4>
                    <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
                </div>

                {/* Parent Work Details with Hover Box */}
                <div className="mb-6 relative">
                    <div className="text-sm font-bold">
                        {t('work.workDetails')}:
                        <span
                            className="text-blue-600 underline cursor-pointer ml-2 break-words"
                            onMouseEnter={() => setIsHoveringDetails(true)}
                            onMouseLeave={() => setIsHoveringDetails(false)}
                            onClick={() => setIsHoveringDetails(!isHoveringDetails)}
                        >
                            {state?.workTitle || "work name"}
                        </span>
                    </div>

                    {isHoveringDetails && workOrder && (
                        <div className="absolute z-50 top-8 left-0 bg-[#6b7ecb] text-white p-4 rounded-lg shadow-xl border border-white w-full md:max-w-2xl transition-all">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-xs md:text-sm font-semibold">
                                <div>{t('work.workOrderNo')} : <strong>{workOrder.workOrderNo}</strong></div>
                                <div>{t('approvals.workStartDate')} : <strong>{workOrder.workStartDate}</strong></div>
                                <div>{t('work.orderDate')} : <strong>{workOrder.workOrderDate}</strong></div>
                                <div>{t('approvals.workEndDate')} : <strong>{workOrder.workEndDate}</strong></div>
                                <div className="sm:col-span-2">{t('approvals.workDuration')} : <strong>{workOrder.duration} Days</strong></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Add/Edit Form */}
                <form className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-8 md:gap-y-4 mb-8 bg-gray-50 p-4 md:p-6 rounded-xl border" onSubmit={handleSubmit}>
                    <div className="md:col-span-2">
                        <InputRow label={t('work.subWorkName')} value={formData.subWorkName} onChange={v => setFormData({ ...formData, subWorkName: v })} />
                    </div>

                    {/* Sub Work Details as TEXTAREA spanning two columns */}
                    <div className="md:col-span-2 flex flex-col md:flex-row items-start text-sm gap-1 md:gap-0">
                        <label className="w-full md:w-40 font-bold text-gray-600 md:text-right md:mt-2">{t('work.subWorkDetails')}</label>
                        <span className="hidden md:inline px-3 font-bold text-gray-800 mt-2">:</span>
                        <textarea
                            className="w-full md:flex-1 border-2 p-2 rounded-lg outline-none focus:border-blue-400 bg-white font-medium min-h-[100px]"
                            value={formData.subWorkDetails}
                            onChange={e => setFormData({ ...formData, subWorkDetails: e.target.value })}
                            required
                        />
                    </div>

                    <InputRow label={t('work.subWorkStartDate')} type="date" value={formData.startDate} onChange={v => setFormData({ ...formData, startDate: v })} />
                    <InputRow label={t('work.subWorkEndDate')} type="date" value={formData.endDate} onChange={v => setFormData({ ...formData, endDate: v })} />

                    <div className="flex flex-col md:flex-row items-start md:items-center text-sm gap-1 md:gap-0">
                        <label className="w-full md:w-40 font-bold text-gray-600 md:text-right">{t('common.attachments')}</label>
                        <span className="hidden md:inline px-3 font-bold text-gray-800">:</span>

                        <div className="flex-1 w-full space-y-2">
                            {/* If editing and a file already exists, show it */}
                            {editingId && formData.fileName && !selectedFile && (
                                <div className="flex items-center gap-2 mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                    <span className="font-semibold text-blue-700 truncate max-w-[200px] ">
                                        Current: {formData.fileName}
                                    </span>
                                    <a
                                        href={resolveMediaUrl(formData.fileUrl)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="staff-btn-save"
                                    >
                                        {t('tables.view')}
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, fileName: "", fileUrl: "" })}
                                        className="text-red-500 hover:text-red-700"
                                        title="Remove existing file"
                                    >
                                        <IoTrashOutline size={16} />
                                    </button>
                                </div>
                            )}

                            {/* The file input for new uploads */}
                            <input
                                type="file"
                                className="w-full text-xs border rounded p-1 bg-white"
                                onChange={e => setSelectedFile(e.target.files[0])}
                            />

                            {selectedFile && (
                                <p className="text-[10px] text-green-600 font-bold italic">
                                    New file selected: {selectedFile.name}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="md:col-span-2 text-center mt-4">
                        <button type="submit" className="staff-btn-save w-full md:w-auto px-12">
                            {editingId ? t('common.update') : t('work.addWork')}
                        </button>
                    </div>
                </form>

                {/* Sub-Work List Table */}
                <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-[10px] md:text-sm ctable">
                        <thead>
                            <tr className="bg-[#a0adf1] text-white">
                                <th className="p-2 border-b border-black">{t('tables.srNo')}</th>
                                <th className="p-2 border-b border-black text-left">{t('work.subWorkName')}</th>
                                <th className="p-2 border-b border-black text-left hidden sm:table-cell">{t('work.subWorkDetails')}</th>
                                <th className="p-2 border-b border-black text-center">{t('work.startDate')}</th>
                                <th className="p-2 border-b border-black text-center">{t('work.endDate')}</th>
                                <th className="p-2 border-b border-black text-center">{t('common.attachments')}</th>
                                <th className="p-2 border-b border-black">{t('tables.edit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subWorks.length === 0 ? (
                                <tr><td colSpan="7" className="text-center py-4 italic text-gray-500">{t('common.noRecords')}</td></tr>
                            ) : (
                                subWorks.map((sw, i) => (
                                    <tr key={sw.id} className="hover:bg-gray-100 transition-colors border-b">
                                        <td className="p-2 border text-center font-medium">{i + 1}</td>
                                        <td className="p-2 border font-semibold">{sw.subWorkName}</td>
                                        <td className="p-2 border hidden sm:table-cell">{sw.subWorkDetails}</td>
                                        <td className="p-2 border text-center whitespace-nowrap">{sw.startDate}</td>
                                        <td className="p-2 border text-center whitespace-nowrap">{sw.endDate}</td>
                                        <td className="p-2 border text-center">
                                            {sw.fileName ? (
                                                <a href={resolveMediaUrl(sw.fileUrl)} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-bold truncate max-w-[80px] md:max-w-[150px] inline-block">
                                                    {sw.fileName}
                                                </a>) : (
                                                <span className='text-gray-500'>{t('common.noFiles')}</span>
                                            )
                                            }
                                        </td>
                                        <td className="p-2 border text-center">
                                            <button className="btnImgEdit mx-auto" onClick={() => handleEdit(sw)}></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <ConfirmActionModal
                open={confirmModal.open}
                onClose={() => setConfirmModal({ open: false })}
                onConfirm={handleConfirmSave}
                title={editingId ? "Confirm Update" : "Confirm Addition"}
                message={`Are you sure you want to ${editingId ? "update" : "add"} these sub work details?`}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />
        </PageFade>
    );
};

// Internal Helper
const InputRow = ({ label, value, onChange, type = "text" }) => (
    <div className="flex items-center text-sm">
        <label className="w-40 font-bold text-gray-600 text-right">{label}</label>
        <span className="px-3 font-bold text-gray-800">:</span>
        <input
            type={type}
            className="flex-1 border-2 p-1.5 rounded-lg outline-none focus:border-blue-400 bg-white font-medium"
            value={value}
            onChange={e => onChange(e.target.value)}
            required
        />
    </div>
);

export default AddWorkForm;